import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal } from "antd";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import OverviewTab from "./tabs/OverviewTab";
import TalentProfileTab from "./tabs/TalentProfileTab";
import InterviewRoundsTab from "./tabs/InterviewRoundsTab";
import FeedbackTab from "./tabs/FeedbackTab";
import DocumentsTab from "./tabs/DocumentsTab";
import CareerPathTab from "./tabs/CareerPathTab";
import { JobApplicationStatusEnum } from "Constants/Enums/JobApplicantStatusEnum";
import { JobPostingStatusEnum } from "Constants/Enums/JobPostingStatusEnum";
import { PageBg, Hero, LoadingState, EmptyState, theme } from "./components/ui";
import { getPostingSkills } from "Common/skillHelpers";

const { confirm } = Modal;

// Per spec: "Not Suitable" button is hidden once the application has progressed
// past the feedback stage (these statuses mean the candidate is being onboarded
// or already finalized).
const REJECT_HIDDEN_FOR = new Set([
  JobApplicationStatusEnum.DocumentSend,
  JobApplicationStatusEnum.DocumentRecieved,
  JobApplicationStatusEnum.OnboardingDone,
  JobApplicationStatusEnum.Hired,
  JobApplicationStatusEnum.Reject,
]);

// Per Mudassir (May-20 call): Shortlist is the action HR takes ONLY when the
// applicant is still at "Applied". After that the pipeline progresses via the
// applicant's actions / interview flow, so the Shortlist button has no role.

function ApplicantDetail() {
  const { jobApplicantId } = useParams();
  const navigate = useNavigate();

  const [applicantData, setApplicantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (!jobApplicantId) return;
    fetchApplicantDetail();
  }, [jobApplicantId]);

  async function fetchApplicantDetail() {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        API.hiringJobApplicant.getById(jobApplicantId)
      );
      setApplicantData(response.data);
    } catch (error) {
      console.error("Error fetching applicant detail:", error);
      toast.error("Failed to load applicant details");
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <PageBg style={{ marginLeft: "250px", marginTop: "20px" }}>
        <LoadingState label="Loading applicant details..." />
      </PageBg>
    );
  }

  if (!applicantData) {
    return (
      <PageBg style={{ marginLeft: "250px", marginTop: "20px" }}>
        <EmptyState title="Applicant not found" icon="🔍" />
      </PageBg>
    );
  }

  const isInternal = applicantData.source === 1;
  const job = applicantData.jobPosting || {};

  // Per Hammad: Talent Profile + AI Insights merged into one tab.
  // Per Mudassir (May-20 call + updated doc):
  //   • Documents tab is EXTERNAL applicants only.
  //   • Career Path tab is INTERNAL applicants only, and only once feedback
  //     has been added (status >= FeedbackAdded).
  const showCareerPathTab =
    isInternal && applicantData.status >= JobApplicationStatusEnum.FeedbackAdded;

  const tabs = [
    { key: "overview", label: "Overview", icon: "👤" },
    { key: "talentProfile", label: "Candidate Profile", icon: "🧠" },
    { key: "interviewRounds", label: "Interview Rounds", icon: "🎯" },
    { key: "feedback", label: "Feedback", icon: "📝" },
    ...(isInternal ? [] : [{ key: "documents", label: "Documents", icon: "📁" }]),
    ...(showCareerPathTab ? [{ key: "careerPath", label: "Career Path", icon: "🚀" }] : []),
  ];

  // ---------- Header action handlers ----------
  const handleShortlist = () => {
    confirm({
      title: "Shortlist this candidate?",
      content: `${applicantData.firstName} ${applicantData.lastName} will be moved forward to the next stage and notified by email.`,
      okText: "Yes, shortlist",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await axiosInstance.post(API.hiringJobApplicant.sendEmail, {
            jobApplicantId: applicantData.jobApplicantId,
          });
          toast.success("Candidate shortlisted and notified.");
          fetchApplicantDetail();
        } catch (err) {
          console.error("Shortlist failed:", err);
          toast.error(
            err.response?.data?.message ||
              "Failed to shortlist. Please try again."
          );
        }
      },
    });
  };

  const handleNotSuitable = () => {
    confirm({
      title: "Reject this candidate?",
      content: `${applicantData.firstName} ${applicantData.lastName} will be marked as Not Suitable and sent a rejection email. This cannot be undone.`,
      okText: "Yes, reject",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          // Backend Reject endpoint is POST (not DELETE) — was returning 405.
          await axiosInstance.post(
            API.hiringJobApplicant.reject(applicantData.jobApplicantId)
          );
          toast.success("Candidate rejected. Notification email queued.");
          setTimeout(() => navigate(-1), 800);
        } catch (err) {
          console.error("Reject failed:", err);
          toast.error(
            err.response?.data?.message ||
              "Failed to reject. Please try again."
          );
        }
      },
    });
  };

  const fullName = `${applicantData.firstName || ""} ${applicantData.lastName || ""}`.trim();

  const heroTitle = (
    <>
      {fullName || "Applicant"}
      {isInternal && (
        <span
          style={{
            background: "rgba(82,196,26,0.85)",
            color: "#fff",
            padding: "4px 14px",
            borderRadius: "40px",
            fontSize: "0.75rem",
            fontWeight: 600,
            letterSpacing: "0.3px",
          }}
        >
          INTERNAL
        </span>
      )}
    </>
  );

  const heroSubtitle = (
    <>
      <div style={{ fontSize: "0.95rem", opacity: 0.95 }}>
        ✉️ {applicantData.email || "—"}
      </div>
      <div style={{ marginTop: "8px" }}>
        <strong>Applied for:</strong> {job.jobTitle || "—"}
      </div>
      <div style={{ fontSize: "0.85rem", opacity: 0.85, marginTop: "2px" }}>
        {[job.departmentName, job.jobLocationType, job.jobType]
          .filter(Boolean)
          .join(" · ") || "—"}
      </div>
    </>
  );

  const postingSkills = getPostingSkills(job);
  const skillBadges =
    postingSkills.length > 0
      ? postingSkills.slice(0, 8).map((s) => `🛠️ ${s}`)
      : [];

  const isRejected = applicantData.status === JobApplicationStatusEnum.Reject;
  // Per Mudassir (May-20 doc): once the posting itself is Filled, ALL hiring
  // pipeline actions before onboarding are blocked — Shortlist, Reject,
  // interview, feedback, document send/receive. Onboarding + later actions
  // remain enabled inside the tabs.
  const isPostingFilled = job?.status === JobPostingStatusEnum.Filled;
  const showShortlist =
    !isPostingFilled && applicantData.status === JobApplicationStatusEnum.Applied;
  const showNotSuitable =
    !isPostingFilled && !REJECT_HIDDEN_FOR.has(applicantData.status);

  // NOTE: Personal Info action lives inside the Documents tab (per doc spec).
  // We don't duplicate it in the hero so the hero stays clean and the
  // documented flow (Documents tab → Personal Info) remains the single path.

  const heroActions =
    showShortlist || showNotSuitable ? (
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {showShortlist && (
          <button type="button" style={heroBtnPrimary} onClick={handleShortlist}>
            ✓ Shortlist
          </button>
        )}
        {showNotSuitable && (
          <button type="button" style={heroBtnGhost} onClick={handleNotSuitable}>
            ✗ Not Suitable
          </button>
        )}
      </div>
    ) : null;

  return (
    <PageBg style={{ marginLeft: "250px", marginTop: "20px" }}>
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          color: theme.accentDeep,
          padding: "8px 18px",
          borderRadius: "40px",
          fontSize: "0.85rem",
          fontWeight: 500,
          marginBottom: "1rem",
          cursor: "pointer",
          boxShadow: theme.shadowCard,
          fontFamily: theme.fontFamily,
        }}
      >
        ← Back
      </button>

      {/* Hero */}
      <Hero
        title={heroTitle}
        subtitle={heroSubtitle}
        badges={skillBadges}
        rightSlot={heroActions}
      />

      {/* Reject banner — locks tab actions */}
      {isRejected && (
        <div
          style={{
            background: "#fff0ee",
            borderRadius: "20px",
            padding: "14px 20px",
            border: "1px solid #f5c2c2",
            borderLeft: "4px solid #b13e3e",
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "14px",
          }}
        >
          <span style={{ fontSize: "1.4rem" }}>🚫</span>
          <div>
            <div
              style={{
                fontWeight: 600,
                fontSize: "0.95rem",
                color: "#7a1f1f",
              }}
            >
              This candidate has been rejected
            </div>
            <div
              style={{
                fontSize: "0.85rem",
                color: "#7a1f1f",
                opacity: 0.85,
                marginTop: "2px",
              }}
            >
              No further actions are available — interview procedures and document
              flows are disabled for rejected applications.
            </div>
          </div>
        </div>
      )}

      {/* Posting Filled banner — blocks hiring pipeline, keeps onboarding open */}
      {isPostingFilled && !isRejected && (
        <div
          style={{
            background: "#fff7ed",
            borderRadius: "20px",
            padding: "14px 20px",
            border: "1px solid #fcd9a3",
            borderLeft: "4px solid #c26b2c",
            marginBottom: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: "14px",
          }}
        >
          <span style={{ fontSize: "1.4rem" }}>🔒</span>
          <div>
            <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#7a4a16" }}>
              This job posting is marked as Filled
            </div>
            <div
              style={{
                fontSize: "0.85rem",
                color: "#7a4a16",
                opacity: 0.9,
                marginTop: "2px",
              }}
            >
              Further hiring pipeline actions are disabled. Onboarding, Personal Info,
              and Career Path Transition for already-progressed applicants remain
              available.
            </div>
          </div>
        </div>
      )}

      {/* Tab nav + content */}
      <div
        style={{
          background: theme.surface,
          borderRadius: "28px",
          boxShadow: theme.shadowCard,
          border: `1px solid ${theme.border}`,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${theme.borderSoft}`,
            padding: "0 12px",
            overflowX: "auto",
          }}
        >
          {tabs.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: "16px 22px",
                  border: "none",
                  background: "transparent",
                  borderBottom: active
                    ? `3px solid ${theme.accent}`
                    : "3px solid transparent",
                  color: active ? theme.accentDeep : theme.textMuted,
                  fontWeight: active ? 600 : 500,
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  fontFamily: theme.fontFamily,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  whiteSpace: "nowrap",
                  transition: "color 0.15s ease, border-color 0.15s ease",
                }}
              >
                <span style={{ fontSize: "1rem" }}>{tab.icon}</span>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Min-height keeps short tabs from leaving a huge empty void below
            the content (per Sara May-23 audit). */}
        <div style={{ background: theme.bg, minHeight: 400 }}>
          {activeTab === "overview" && <OverviewTab applicantData={applicantData} />}
          {activeTab === "talentProfile" && (
            <TalentProfileTab
              jobApplicationId={jobApplicantId}
              jobPostingSkills={getPostingSkills(job)}
              isInternal={isInternal}
            />
          )}
          {activeTab === "interviewRounds" && (
            <InterviewRoundsTab
              jobApplicationId={jobApplicantId}
              isRejected={isRejected}
              isPostingFilled={isPostingFilled}
            />
          )}
          {activeTab === "feedback" && (
            <FeedbackTab
              jobApplicationId={jobApplicantId}
              applicantData={applicantData}
              onFeedbackPosted={fetchApplicantDetail}
              isPostingFilled={isPostingFilled}
            />
          )}
          {activeTab === "documents" && (
            <DocumentsTab
              jobApplicationId={jobApplicantId}
              applicantData={applicantData}
              isPostingFilled={isPostingFilled}
            />
          )}
          {activeTab === "careerPath" && (
            <CareerPathTab
              jobApplicationId={jobApplicantId}
              fallbackTargetTitle={job.jobTitle}
              applicantStatus={applicantData.status}
            />
          )}
        </div>
      </div>
    </PageBg>
  );
}

const heroBtnPrimary = {
  background: "rgba(255,255,255,0.95)",
  color: "#0f5a2a",
  border: "none",
  padding: "10px 22px",
  borderRadius: "40px",
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
  fontFamily: "inherit",
};

// Destructive action — white background with deep-red text reads clearly
// as "danger" against the green hero without clashing the way a saturated
// red fill does. Same shape as the primary button next to it.
const heroBtnGhost = {
  background: "rgba(255,255,255,0.95)",
  color: "#b91c1c",
  border: "1.5px solid rgba(255,255,255,0.95)",
  padding: "10px 22px",
  borderRadius: "40px",
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
};

export default ApplicantDetail;
